import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Loader2, ArrowLeft, Send, CheckCircle2 } from "lucide-react";
import { SITE } from "@/lib/site-config";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    document.title = `Passwort vergessen · ${SITE.brand.name}`;
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/forgot-password`, { email: email.trim() }, { timeout: 15000 });
      setMsg(data.message || "Falls ein Konto existiert, wurde ein Link gesendet.");
      setSent(true);
    } catch (err) {
      setMsg("Falls ein Konto mit dieser E-Mail-Adresse existiert, wurde ein Link zum Zurücksetzen gesendet.");
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#5C6B49] text-[#F3F1E8] flex items-center justify-center px-4" data-testid="forgot-password-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
        <button type="button" onClick={() => navigate("/admin/login")} className="inline-flex items-center gap-2 text-[#C9C6B4] hover:text-[#F3F1E8] text-xs font-bold uppercase tracking-wider mb-6 transition-colors" data-testid="back-to-login-btn">
          <ArrowLeft size={12} /> Zum Login
        </button>
        <div className="flex justify-center mb-8"><Logo size={72} showWordmark={false} /></div>
        <div className="glass-strong rounded-2xl p-7 sm:p-9">
          {sent ? (
            <div className="text-center" data-testid="forgot-sent-message">
              <CheckCircle2 size={40} className="text-[#B8CFA0] mx-auto mb-4" />
              <h1 className="font-display text-2xl font-black tracking-tight mb-3">E-Mail unterwegs</h1>
              <p className="text-sm text-[#C9C6B4] leading-relaxed">{msg}</p>
              <button onClick={() => navigate("/admin/login")} className="mt-6 w-full bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold uppercase tracking-wider rounded-xl px-6 py-3 transition-all">
                Zurück zum Login
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Mail size={14} className="text-[#E8E5D0]" />
                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#E8E5D0]">Passwort vergessen</span>
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight leading-tight mb-2">Zurücksetzen anfordern</h1>
              <p className="text-sm text-[#C9C6B4] mb-6">Gib die E-Mail-Adresse deines Admin-Kontos ein. Wir senden dir einen Link zum Zurücksetzen.</p>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-password-form">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#C9C6B4] mb-1.5 block">E-Mail-Adresse</label>
                  <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="w-full bg-[#4E5A3B]/60 border border-white/15 hover:border-white/25 focus:border-[#E8E5D0] focus:ring-2 focus:ring-[#E8E5D0]/25 outline-none rounded-lg px-4 py-3 text-[#F3F1E8] text-[15px] placeholder:text-[#A8A58F] transition-colors"
                    placeholder="name@domain.de" data-testid="forgot-email-input" />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-[#E8E5D0] hover:bg-[#D6D3BF] disabled:opacity-50 text-[#3A4230] font-extrabold uppercase tracking-wider rounded-xl px-6 py-3.5 flex items-center justify-center gap-2 transition-all"
                  data-testid="forgot-submit-btn">
                  {loading ? <><Loader2 className="animate-spin" size={16} /> Senden…</> : <><Send size={16} /> Link anfordern</>}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
